import { useEffect, useState, useMemo } from "react";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { UniversalTable } from "shared/components/UniversalTable";
import { UniversalTableSearch } from "shared/components/UniversalTable/components/UniversalTableSearch";
import { TablePagination } from "components/TablePagination";
import { useOpexInvoices, OpexViewMode } from "./hooks/useOpexInvoices";
import { OpexViewTabs } from "./components/OpexViewTabs";
import {
  FileText,
  X,
  Link as LinkIcon,
  Archive,
  ArrowCounterClockwise,
} from "@phosphor-icons/react";
import { toast } from "react-toastify";
import { TicketPickerModal } from "./components/TicketPickerModal";
import {
  createOpexInvoiceCells,
  LinkedTicket,
  TicketSuggestion,
} from "./shared/opexInvoiceCells";

interface OpexInvoice {
  /**
   * Odoo account.move.line — de factuurregel zelf. Koppelen gebeurt hierop en niet
   * meer op de analytische (per-unit) lijn: één factuurregel valt uiteen in tot 74
   * analytische lijnen, waardoor één interventie van EUR 150 als 3 regels van
   * EUR 50 in de lijst stond.
   */
  moveLineId?: number;
  /** Kostenplaatsen waarop de regel geboekt staat (plan "Gebouwen"). */
  kostenplaatsen?: Array<{ id: number; name: string; amount: number }>;
  /** Gebouwen achter die kostenplaatsen, bv. ['2200-NONN-59']. */
  buildings?: string[];
  /** Of de factuur een PDF heeft. De PDF zelf wordt op aanvraag opgehaald. */
  hasPdf?: boolean;
  id: number; // = moveLineId (alias)
  databaseId?: number; // Database ID (only for imported invoices, used for unlinking)
  partner_id: [number, string];
  date: string;
  name: string; // Line description
  move_name: string; // Invoice number
  move_line_id: [number, string];
  move_id?: number; // Invoice ID for Odoo link
  amount: number;
  general_account_id: [number, string];
  account_id: [number, string]; // Analytic account (building/unit)
  analytic_distribution?: { [key: string]: { percentage: number; name: string } }; // Analytic distribution with names
  linkedTicket?: LinkedTicket | null;
  /** Normaal één; oude data kan één boekingsregel aan meerdere tickets hebben. */
  linkedTickets?: LinkedTicket[];
  isLinked?: boolean;
  isArchived?: boolean;
  archivedReason?: string | null;
  invoice_pdf?: {
    name: string;
    data: string;
  };
  isImported?: boolean; // Flag to distinguish imported (in database) vs unimported (from Odoo)
}

/**
 * Het gebouw van een factuurregel — maar enkel wanneer het er onmiskenbaar één is.
 *
 * 99,5% van de regels boekt binnen één gebouw. Voor de rest is er geen "het"
 * gebouw, en dan is voorfilteren erger dan niet voorfilteren: het juiste ticket
 * zou er stil uit gefilterd worden.
 */
const enigGebouw = (buildings?: string[]): string | null =>
  buildings && buildings.length === 1 ? buildings[0] : null;

export const OpexInvoicesPage = () => {
  const { session } = useSession();
  const [selectedBuilding, setSelectedBuilding] = useState<string>("");
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);
  const [selectedLineIds, setSelectedLineIds] = useState<Set<number>>(new Set());
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [bulkLinkMode, setBulkLinkMode] = useState(false);
  /**
   * Het gebouw van de factuurregel(s) waarvoor de ticketkiezer open staat.
   * Zonder dit toonde "ander ticket…" tickets van álle gebouwen en moest je eerst
   * zelf de gebouwnaam intikken.
   */
  const [linkModalBuildingId, setLinkModalBuildingId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  // Gedebounced: elke toetsaanslag zou anders een nieuwe Odoo-query starten.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  /** Kandidaat-tickets per boekingsregel, opgehaald voor de zichtbare regels. */
  const [suggestions, setSuggestions] = useState<Record<number, TicketSuggestion[]>>({});
  const [viewMode, setViewMode] = useState<OpexViewMode>("unlinked");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchText]);
  const [selectedInvoiceForPreview, setSelectedInvoiceForPreview] = useState<OpexInvoice | null>(null);

  // Initialize sticky top as 0 for this page (no global SearchBar)
  useEffect(() => {
    document.documentElement.style.setProperty('--sticky-top', '0px');
  }, []);

  const PAGE_SIZE = 50;
  const {
    invoices,
    setInvoices,
    totalCount,
    counts,
    isLoading,
    error: fetchError,
    refresh,
  } = useOpexInvoices<OpexInvoice>({
    viewMode,
    buildingId: selectedBuilding || undefined,
    search: debouncedSearch,
    page,
    pageSize: PAGE_SIZE,
  });

  useEffect(() => {
    if (fetchError) toast.error("Fout bij ophalen OPEX facturen");
  }, [fetchError]);

  // Terug naar pagina 1 wanneer de inhoud van de lijst verandert, anders sta je op
  // een pagina die in de nieuwe selectie niet bestaat.
  useEffect(() => {
    setPage(1);
  }, [viewMode, selectedBuilding, debouncedSearch]);

  /**
   * Haal ticketvoorstellen op voor de regels die nog niet gekoppeld zijn.
   *
   * Alleen voor wat geladen is, en alleen voor wat nog geen voorstel heeft, zodat
   * lazy loading niet telkens alles opnieuw opvraagt.
   */
  useEffect(() => {
    if (!session) return;

    const missing = invoices
      .filter((invoice) => !invoice.isLinked && !invoice.linkedTicket)
      .map((invoice) => invoice.moveLineId ?? invoice.id)
      .filter((moveLineId) => suggestions[moveLineId] === undefined);

    if (missing.length === 0) return;

    let cancelled = false;

    (async () => {
      try {
        // Gebundeld per 100 zodat de query-string beheersbaar blijft.
        const batch = missing.slice(0, 100);
        const response = await fetchWithAuth(
          `${API_URL}/helpdesk/opex-invoices/ticket-suggestions?moveLineIds=${batch.join(",")}`,
          { method: "GET" },
          session,
        );

        if (!response.ok || cancelled) return;

        const result: Record<number, TicketSuggestion[]> = await response.json();
        setSuggestions((prev) => {
          const next = { ...prev };
          // Ook lege resultaten vastleggen, anders blijft dit heropvragen.
          batch.forEach((moveLineId) => {
            next[moveLineId] = result[moveLineId] ?? [];
          });
          return next;
        });
      } catch (error) {
        // Voorstellen zijn een hulpmiddel: falen mag de lijst niet breken.
        console.error("Error fetching ticket suggestions:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [invoices, session, suggestions]);

  // Gebouwen voor het filter, uit de kostenplaatsen van de geladen regels.
  // De sleutel is de NAAM van het gebouw (bv. "2200-NONN-59"), niet het Odoo
  // account-id: de backend filtert op naam met een prefix-match, zodat het gebouw
  // én al zijn units meekomen. Voorheen werd het account-id meegestuurd, wat nooit
  // matchte — en het werd uit account_id gehaald, dat in Odoo 18 altijd leeg is.
  const buildingOptions = useMemo(() => {
    const buildings = new Set<string>();
    invoices.forEach((invoice) => {
      (invoice.buildings ?? []).forEach((building) => buildings.add(building));
    });
    return Array.from(buildings)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ key: name, label: name }));
  }, [invoices]);

  /**
   * Haal de PDF op wanneer de gebruiker hem effectief nodig heeft.
   *
   * De lijst stuurt alleen een `hasPdf`-vlag mee: de hoofdbijlagen van alle
   * OPEX-facturen samen zijn ~272 MB, en die werden voorheen bij élk paginaverzoek
   * volledig als base64 meegestuurd.
   */
  const fetchInvoicePdf = async (
    invoice: OpexInvoice,
  ): Promise<{ name: string; data: string } | null> => {
    if (invoice.invoice_pdf) return invoice.invoice_pdf;
    if (!session) return null;

    const moveLineId = invoice.moveLineId ?? invoice.id;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/opex-invoices/${moveLineId}/pdf`,
        { method: "GET" },
        session,
      );

      if (!response.ok) {
        console.error("Failed to fetch invoice PDF", {
          moveLineId,
          status: response.status,
        });
        return null;
      }

      const pdf = await response.json();
      // Bewaar op de rij zodat een tweede klik geen nieuwe download doet.
      setInvoices((prev) =>
        prev.map((inv) =>
          inv.id === invoice.id ? { ...inv, invoice_pdf: pdf } : inv,
        ),
      );
      return pdf;
    } catch (error) {
      console.error("Error fetching invoice PDF:", error);
      return null;
    }
  };

  const handleDownloadPdf = async (invoice: OpexInvoice) => {
    if (invoice.hasPdf === false && !invoice.invoice_pdf) {
      toast.error("Geen PDF beschikbaar voor deze factuur");
      return;
    }

    const pdf = await fetchInvoicePdf(invoice);
    if (!pdf) {
      toast.error("Geen PDF beschikbaar voor deze factuur");
      return;
    }

    try {
      // Convert base64 to blob
      const byteCharacters = atob(pdf.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = pdf.name || `${invoice.move_name}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("PDF succesvol gedownload");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Fout bij downloaden PDF");
    }
  };

  const handleRowClick = async (invoice: OpexInvoice) => {
    // Alleen previewen als er een PDF is; die wordt nu op aanvraag opgehaald.
    if (invoice.hasPdf === false && !invoice.invoice_pdf) return;

    const pdf = await fetchInvoicePdf(invoice);
    if (pdf) {
      setSelectedInvoiceForPreview({ ...invoice, invoice_pdf: pdf });
      return;
    }

    if (invoice.invoice_pdf) {
      setSelectedInvoiceForPreview(invoice);
    }
  };

  const handleLinkInvoice = async (analyticLineId: number, ticketId: number) => {
    if (!session) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/opex-invoices/${analyticLineId}/link-ticket`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ticketId })
        },
        session
      );

      if (response.ok) {
        const linkedTicket = await response.json();

        // Update local state - update the invoice to show it's linked.
        // Ook `linkedTickets` bijwerken: de cellen lezen die lijst eerst, en de
        // backend stuurt voor een ongekoppelde regel een lege array mee (geen
        // undefined), dus zonder dit bleef de rij er ongekoppeld uitzien.
        setInvoices((prev) =>
          prev.map((inv) =>
            inv.id === analyticLineId
              ? {
                  ...inv,
                  linkedTicket,
                  linkedTickets: [linkedTicket],
                  isLinked: true,
                  databaseId: linkedTicket.invoiceLineDbId,
                  isImported: true
                }
              : inv
          )
        );

        // In "nog te koppelen" hoort de regel nu te verdwijnen; tellers mee bij.
        refresh();
        // Bewust géén toast: de rij springt meteen op groen met het ticketnummer
        // erin, dus de melding zei niets extra's en bleef in de weg staan bij het
        // koppelen van de volgende regel. Fouten melden we wél.
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        toast.error(errorData.message || "Fout bij koppelen factuurlijn aan ticket");
      }
    } catch (error) {
      console.error("Error linking invoice line to ticket:", error);
      toast.error("Fout bij koppelen factuurlijn aan ticket");
    }
  };

  const handleUnlinkInvoice = async (databaseId: number, odooAnalyticLineId: number) => {
    if (!session) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/opex-invoices/${databaseId}/unlink-ticket`,
        {
          method: "PUT"
        },
        session
      );

      if (response.ok) {
        // Update local state - convert back to unimported status
        setInvoices((prev) =>
          prev.map((inv) =>
            inv.id === odooAnalyticLineId
              ? {
                  ...inv,
                  linkedTicket: null,
                  linkedTickets: [],
                  isLinked: false,
                  isImported: false,
                  databaseId: undefined,
                }
              : inv
          )
        );

        refresh();
        toast.success("Factuurlijn succesvol ontkoppeld van ticket");
      } else {
        toast.error("Fout bij ontkoppelen factuurlijn van ticket");
      }
    } catch (error) {
      console.error("Error unlinking invoice line from ticket:", error);
      toast.error("Fout bij ontkoppelen factuurlijn van ticket");
    }
  };

  /**
   * Koppel alle geselecteerde factuurregels aan één ticket — een factuur kan over
   * meerdere interventies gaan. Eén verzoek in plaats van één per regel; de backend
   * verwerkt ze afzonderlijk en rapporteert per regel wat er mislukte.
   */
  const handleBulkLinkInvoices = async (ticketId: number) => {
    if (!session || selectedLineIds.size === 0) return;

    const moveLineIds = Array.from(selectedLineIds);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/opex-invoices/link-tickets`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ moveLineIds, ticketId }),
        },
        session,
      );

      if (!response.ok) {
        const body = await response.text().catch(() => response.statusText);
        throw new Error(body);
      }

      const result: {
        linked: number[];
        failed: Array<{ moveLineId: number; reason: string }>;
      } = await response.json();

      const linked = new Set(result.linked);
      setInvoices((prev) =>
        prev.map((inv) =>
          linked.has(inv.moveLineId ?? inv.id)
            ? { ...inv, isLinked: true, isImported: true }
            : inv,
        ),
      );
      setSelectedLineIds(new Set());

      refresh();

      if (result.linked.length > 0) {
        // Kort: bij een bulkkoppeling is het aantal wél nieuwe informatie, maar
        // de melding mag het koppelen van de volgende reeks niet ophouden.
        toast.success(
          `${result.linked.length} factuurlijn(en) gekoppeld aan ticket H${ticketId}`,
          { autoClose: 1500 },
        );
      }
      if (result.failed.length > 0) {
        console.warn("Some invoice lines could not be linked", result.failed);
        toast.error(
          `${result.failed.length} factuurlijn(en) niet gekoppeld: ${result.failed[0].reason}`,
        );
      }
    } catch (error) {
      console.error("Error bulk linking invoice lines to ticket:", error);
      toast.error("Fout bij koppelen van de geselecteerde factuurlijnen");
    }
  };

  // Select all lines from the same invoice
  const handleSelectAllFromInvoice = (invoiceNumber: string) => {
    const linesFromInvoice = invoices.filter(inv => inv.move_name === invoiceNumber);
    const lineIds = linesFromInvoice.map(inv => inv.id);

    // Check if all are already selected
    const allSelected = lineIds.every(id => selectedLineIds.has(id));

    setSelectedLineIds(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        // Deselect all
        lineIds.forEach(id => newSet.delete(id));
      } else {
        // Select all
        lineIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  };



  const renderRow = (
    invoice: OpexInvoice,
    context: { isSelected: boolean; onToggleSelect: (selected: boolean) => void },
  ) => {
    const moveLineId = invoice.moveLineId ?? invoice.id;

    return createOpexInvoiceCells({
      invoice: {
        moveLineId,
        moveId: invoice.move_id,
        moveName: invoice.move_name,
        date: invoice.date,
        description: invoice.name,
        partnerName: invoice.partner_id?.[1] ?? "Onbekend",
        amountExclVat: Math.abs(invoice.amount ?? 0),
        kostenplaatsen: invoice.kostenplaatsen,
        buildings: invoice.buildings,
        hasPdf: invoice.hasPdf ?? !!invoice.invoice_pdf,
        linkedTicket: invoice.linkedTicket,
        linkedTickets: invoice.linkedTickets,
        isArchived: !!invoice.isArchived,
        archivedReason: invoice.archivedReason ?? null,
      },
      suggestions: suggestions[moveLineId],
      rowContext: context,
      handlers: {
        onOpenPdf: () => handleRowClick(invoice),
        onPickTicket: () => {
          setSelectedInvoiceId(moveLineId);
          setLinkModalBuildingId(enigGebouw(invoice.buildings));
          setLinkModalOpen(true);
        },
        onAcceptSuggestion: (ticketId) => handleLinkInvoice(moveLineId, ticketId),
        onUnlink: (invoiceLineDbId) =>
          handleUnlinkInvoice(invoiceLineDbId, moveLineId),
        // In het archief is de rij-actie terugzetten; elders archiveren.
        onArchive: () => handleArchiveOne(moveLineId),
        onUnarchive: () => handleUnarchive(moveLineId),
      },
    });
  };

  /**
   * Kolommen. De omschrijving krijgt het meeste gewicht: dat is waarop tickets
   * herkend worden. De ticketkolom is breed genoeg voor de voorstel-chips.
   */
  const columnsConfig = {
    columns: [
      { id: "select", label: "", minWidth: 40, defaultWeight: 0 },
      { id: "description", label: "Omschrijving", minWidth: 220, defaultWeight: 4 },
      { id: "partner", label: "Leverancier", minWidth: 140, defaultWeight: 2 },
      { id: "date", label: "Datum", minWidth: 100, defaultWeight: 1 },
      { id: "kostenplaats", label: "Kostenplaats", minWidth: 160, defaultWeight: 2 },
      { id: "amount", label: "Bedrag", minWidth: 100, defaultWeight: 1 },
      { id: "invoice", label: "Factuur", minWidth: 130, defaultWeight: 1 },
      { id: "ticket", label: "Ticket", minWidth: 220, defaultWeight: 4.5 },
      { id: "pdf", label: "PDF", minWidth: 60, defaultWeight: 0 },
      { id: "actions", label: "", minWidth: 50, defaultWeight: 0 },
    ],
    // v4: de kolom "Ticketlabel" is weer weg — het label staat nu in de
    // ticketchip zelf (ID, label, titel), en "ticket" erft de vrijgekomen ruimte.
    localStorageKey: "opex-invoices-column-widths-v4",
  };

  // Filter sections for building filter
  const filterSections = useMemo(() => [
    {
      key: 'building',
      label: 'Gebouw',
      options: buildingOptions,
      color: 'moss' as const,
      multiSelect: false
    }
  ], [buildingOptions]);

  /**
   * Archiveer de geselecteerde factuurregels: ze horen niet bij een ticket en moeten
   * uit de werkvoorraad. De reden wordt bewaard zodat later navolgbaar is waarom.
   */
  const handleArchiveSelected = async (selection?: Set<number>) => {
    const target = selection ?? selectedLineIds;
    if (!session || target.size === 0) return;

    const reason = window.prompt(
      `Waarom horen deze ${target.size} factuurregel(s) niet bij een ticket?\n(bv. vast onderhoudscontract, poetsdienst)`,
      "",
    );
    // Geannuleerd (null) betekent niet archiveren; een lege reden mag wel.
    if (reason === null) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/opex-invoices/archive`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            moveLineIds: Array.from(target),
            reason: reason.trim() || undefined,
          }),
        },
        session,
      );

      if (!response.ok) {
        throw new Error(await response.text().catch(() => response.statusText));
      }

      const result: {
        archived: number[];
        failed: Array<{ moveLineId: number; reason: string }>;
      } = await response.json();

      setSelectedLineIds(new Set());
      refresh();

      if (result.archived.length > 0) {
        toast.success(`${result.archived.length} factuurregel(s) gearchiveerd`);
      }
      if (result.failed.length > 0) {
        console.warn("Some invoice lines could not be archived", result.failed);
        toast.error(
          `${result.failed.length} niet gearchiveerd: ${result.failed[0].reason}`,
        );
      }
    } catch (error) {
      console.error("Error archiving invoice lines:", error);
      toast.error("Fout bij archiveren van de factuurregels");
    }
  };

  /** Archiveer één factuurregel, met dezelfde vraag om een reden. */
  const handleArchiveOne = (moveLineId: number) =>
    handleArchiveSelected(new Set([moveLineId]));

  /** Terug naar de werkvoorraad. */
  const handleUnarchive = async (moveLineId: number) => {
    if (!session) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/opex-invoices/${moveLineId}/unarchive`,
        { method: "PUT" },
        session,
      );

      if (!response.ok) {
        throw new Error(await response.text().catch(() => response.statusText));
      }

      refresh();
      toast.success("Factuurregel terug in de werkvoorraad");
    } catch (error) {
      console.error("Error unarchiving invoice line:", error);
      toast.error("Fout bij terugzetten van de factuurregel");
    }
  };

  // Bulk actions for selected lines
  const bulkActions = useMemo(() => {
    const requireSelection = (action: () => void) => () => {
      if (selectedLineIds.size === 0) {
        toast.error("Selecteer minstens één factuurlijn");
        return;
      }
      action();
    };

    // In het archief is koppelen niet aan de orde; daar staat terugzetten.
    if (viewMode === "archived") {
      return [
        {
          label: 'Terug naar werkvoorraad',
          onClick: requireSelection(async () => {
            for (const moveLineId of Array.from(selectedLineIds)) {
              await handleUnarchive(moveLineId);
            }
            setSelectedLineIds(new Set());
          }),
          variant: 'secondaryNeutral' as const,
          icon: <ArrowCounterClockwise size={16} weight="bold" />,
        },
      ];
    }

    return [
      {
        label: 'Koppel aan ticket',
        onClick: requireSelection(() => {
          // Delen alle geselecteerde regels hetzelfde gebouw, dan filtert de
          // kiezer daarop voor; zo niet, dan blijft hij open op alle gebouwen.
          const gebouwen = new Set(
            invoices
              .filter((invoice) => selectedLineIds.has(invoice.moveLineId ?? invoice.id))
              .map((invoice) => enigGebouw(invoice.buildings))
              .filter((gebouw): gebouw is string => !!gebouw),
          );
          setLinkModalBuildingId(gebouwen.size === 1 ? [...gebouwen][0] : null);
          setBulkLinkMode(true);
          setLinkModalOpen(true);
        }),
        variant: 'primaryMoss' as const,
        icon: <LinkIcon size={16} weight="bold" />
      },
      {
        label: 'Archiveer',
        onClick: requireSelection(handleArchiveSelected),
        variant: 'secondaryNeutral' as const,
        icon: <Archive size={16} weight="bold" />,
      },
    ];
  }, [selectedLineIds, viewMode, session]);

  return (
    <div className="mb-10 flex flex-col gap-0 bg-brick-secondary-fine min-h-screen">
      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-4">
              <div className="w-8 h-8 border-4 border-moss-primary/20 border-t-moss-primary rounded-full animate-spin"></div>
              <p className="text-sm text-neutral-600">Facturen aan het laden...</p>
            </div>
          </div>
        ) : (
          <>
            <OpexViewTabs
              viewMode={viewMode}
              counts={counts}
              onChange={setViewMode}
            />

            <UniversalTableSearch
              rows={invoices}
              searchValue={searchText}
              onSearchChange={setSearchText}
              searchPlaceholder="Zoek op omschrijving, leverancier of factuurnummer..."
              filterSections={filterSections}
              activeFilters={{ building: selectedBuilding ? [selectedBuilding] : [] }}
              onFilterChange={(key, values) => {
                if (key === 'building') {
                  setSelectedBuilding(values.length > 0 ? values[0] : '');
                }
              }}
              selectedCount={selectedLineIds.size}
              onClearSelection={() => setSelectedLineIds(new Set())}
              bulkActions={bulkActions}
            />

            {/*
              Geen groepering: 95% van de facturen heeft precies één OPEX-regel,
              dus de accordeon kostte een klik per rij zonder iets te tonen.
            */}
            <div className="mb-2 flex items-center justify-end">
              <TablePagination
                page={page}
                pageSize={PAGE_SIZE}
                totalCount={totalCount}
                onPageChange={setPage}
                isLoading={isLoading}
              />
            </div>

            <UniversalTable
              rows={invoices}
              title="Facturen OPEX"
              description={`${totalCount} factuurregels · rekeningen 611020 / 611021`}
              renderRow={renderRow}
              columnsConfig={columnsConfig}
              selectedRows={selectedLineIds}
              onSelectionChange={setSelectedLineIds}
              onRowClick={handleRowClick}
            />

            <div className="mt-3 flex items-center justify-end">
              <TablePagination
                page={page}
                pageSize={PAGE_SIZE}
                totalCount={totalCount}
                onPageChange={setPage}
                isLoading={isLoading}
              />
            </div>
          </>
        )}
      </div>

      {linkModalOpen && (
        <TicketPickerModal
          isOpen={linkModalOpen}
          // Het gebouw van de factuurregel, met de gebouwfilter van de pagina als
          // terugval: de kiezer opent dan meteen op de juiste tickets.
          buildingId={linkModalBuildingId ?? selectedBuilding ?? undefined}
          onClose={() => {
            setLinkModalOpen(false);
            setSelectedInvoiceId(null);
            setBulkLinkMode(false);
            setLinkModalBuildingId(null);
          }}
          title={bulkLinkMode
            ? `Selecteer ticket om ${selectedLineIds.size} factuurlijn(en) te koppelen`
            : "Selecteer ticket om te koppelen"
          }
          onConfirm={async (ticket) => {
            if (bulkLinkMode) {
              await handleBulkLinkInvoices(ticket.id);
              setBulkLinkMode(false);
            } else if (selectedInvoiceId) {
              await handleLinkInvoice(selectedInvoiceId, ticket.id);
              setSelectedInvoiceId(null);
            }
            setLinkModalOpen(false);
            setLinkModalBuildingId(null);
          }}
        />
      )}

      {/* PDF Preview Modal — gecentreerd en groot, voor alle schermgroottes */}
      {selectedInvoiceForPreview && selectedInvoiceForPreview.invoice_pdf && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[95vh] flex flex-col">
            {/* Header */}
            <div className="shrink-0 p-4 border-b border-neutral-200 flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-neutral-primary truncate">
                  {selectedInvoiceForPreview.move_name}
                </h3>
                <p className="text-xs text-neutral-secondary-mid mt-0.5">
                  {selectedInvoiceForPreview.partner_id[1]}
                </p>
              </div>
              <button
                onClick={() => setSelectedInvoiceForPreview(null)}
                className="ml-2 p-2 hover:bg-neutral-100 rounded-full transition-colors"
                aria-label="Sluit preview"
              >
                <X size={20} />
              </button>
            </div>

            {/* PDF Viewer — min-h-0 zodat de knoppenbalk hieronder niet buiten
                het scherm geduwd wordt op een korter scherm */}
            <div className="min-h-0 flex-1 overflow-auto bg-gray-100">
              <iframe
                src={`data:application/pdf;base64,${selectedInvoiceForPreview.invoice_pdf.data}`}
                className="w-full h-full border-0 min-h-[300px]"
                title={`PDF Preview: ${selectedInvoiceForPreview.move_name}`}
              />
            </div>

            {/* Footer with actions */}
            <div className="shrink-0 p-4 border-t border-neutral-200 flex gap-2">
              <button
                onClick={() => handleDownloadPdf(selectedInvoiceForPreview)}
                className="flex-1 px-4 py-3 bg-moss-primary text-white rounded-lg hover:bg-moss-primaryDark transition-colors text-sm font-medium flex items-center justify-center gap-2"
              >
                <FileText size={16} weight="bold" />
                Download
              </button>
              <button
                onClick={() => {
                  handleSelectAllFromInvoice(selectedInvoiceForPreview.move_name);
                  setSelectedInvoiceForPreview(null);
                }}
                className="flex-1 px-4 py-3 bg-brick-primary text-white rounded-lg hover:bg-brick-primaryDark transition-colors text-sm font-medium"
              >
                Selecteer alles
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
